/**
 * AutoTranslator — automatically translates the entire DOM when language changes.
 *
 * Mount once near the root (inside LanguageProvider).
 * Walks all text nodes, batches them to /api/translate, then replaces text in-place.
 * Uses MutationObserver to handle dynamic content (route changes, lazy loads).
 *
 * - Skips: <script>, <style>, <code>, <pre>, [data-no-translate], inputs, currency/numbers
 * - Caches: server (MongoDB) + localStorage + in-memory
 */

import { useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = 'https://api.bharatdarshan.app/api/translate';
const LOCAL_PREFIX = 'bd_dom_tx:';

const memCache = new Map(); // 'lang:hash' -> translated
const originalText = new WeakMap(); // textNode -> originalEnglish

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION', 'SVG', 'PATH', 'CANVAS']);

const hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return String(h);
};
const ck = (text, lang) => `${lang}:${hash(text)}`;

// Decide if a text node is translatable
const isTranslatable = (text) => {
  if (!text) return false;
  const t = text.trim();
  if (t.length < 2) return false;
  // Skip pure numbers / currency / dates
  if (/^[\d\s,.\-+:/%₹$€£¥]+$/.test(t)) return false;
  // Skip if contains no Latin letters (already translated)
  if (!/[a-zA-Z]/.test(t)) return false;
  return true;
};

const isInsideSkip = (node) => {
  let p = node.parentNode;
  while (p) {
    if (p.nodeType === 1) {
      if (SKIP_TAGS.has(p.tagName)) return true;
      if (p.hasAttribute && p.hasAttribute('data-no-translate')) return true;
      if (p.contentEditable === 'true') return true;
    }
    p = p.parentNode;
  }
  return false;
};

const loadLocal = (text, lang) => {
  const k = ck(text, lang);
  if (memCache.has(k)) return memCache.get(k);
  try {
    const v = localStorage.getItem(LOCAL_PREFIX + k);
    if (v) { memCache.set(k, v); return v; }
  } catch {}
  return null;
};
const saveLocal = (text, lang, translated) => {
  const k = ck(text, lang);
  memCache.set(k, translated);
  try { localStorage.setItem(LOCAL_PREFIX + k, translated); } catch {}
};

// Walk all text nodes in subtree
function collectTextNodes(root) {
  const out = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      if (isInsideSkip(node)) return NodeFilter.FILTER_REJECT;
      if (!isTranslatable(node.nodeValue)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  while ((n = walker.nextNode())) out.push(n);
  return out;
}

async function translateApi(texts, targetLang) {
  try {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ texts, targetLang }),
    });
    const data = await res.json();
    if (data && Array.isArray(data.translations)) return data.translations;
    return texts;
  } catch {
    return texts;
  }
}

// In-flight to debounce
let pendingTimer = null;

async function translateRoot(root, lang) {
  // Collect ALL text nodes (no isTranslatable filter so we also re-visit nodes
  // that currently hold non-English text). We rely on the originalText
  // WeakMap to recover the original English source text.
  const nodes = collectTextNodesAll(root);
  if (!nodes.length) return;

  if (lang === 'en') {
    // Restore originals (saved when we first translated them)
    nodes.forEach((n) => {
      const orig = originalText.get(n);
      if (orig != null && n.nodeValue !== orig) n.nodeValue = orig;
    });
    return;
  }

  // Decide source text for each node: prefer WeakMap original, otherwise
  // current value (but only if it is translatable English).
  const nodeSources = []; // { node, source }
  nodes.forEach((n) => {
    let src = originalText.get(n);
    if (src == null) {
      // Never seen this node before. Only seed it if the current value looks
      // like translatable English text — otherwise (e.g. already-translated
      // Hindi/Marathi from server, or numbers/symbols) skip it.
      if (!isTranslatable(n.nodeValue)) return;
      src = n.nodeValue;
      originalText.set(n, src);
    }
    nodeSources.push({ node: n, source: src });
  });

  if (!nodeSources.length) return;

  // Apply cached translations; collect unknowns
  const unknown = [];
  const unknownEntries = [];
  nodeSources.forEach(({ node, source }) => {
    const trimmed = source.trim();
    const cached = loadLocal(trimmed, lang);
    if (cached) {
      const m = source.match(/^(\s*)([\s\S]*?)(\s*)$/);
      const lead = m ? m[1] : '';
      const tail = m ? m[3] : '';
      const next = lead + cached + tail;
      if (node.nodeValue !== next) node.nodeValue = next;
    } else {
      unknown.push(trimmed);
      unknownEntries.push({ node, source });
    }
  });

  if (unknown.length === 0) return;

  // Dedupe unknowns for API efficiency
  const uniqueMap = new Map();
  unknown.forEach((t, i) => {
    if (!uniqueMap.has(t)) uniqueMap.set(t, []);
    uniqueMap.get(t).push(i);
  });
  const uniqueTexts = Array.from(uniqueMap.keys());

  const BATCH = 100;
  const allTranslated = {};
  for (let i = 0; i < uniqueTexts.length; i += BATCH) {
    const batch = uniqueTexts.slice(i, i + BATCH);
    const res = await translateApi(batch, lang);
    batch.forEach((src, j) => {
      const tr = res[j] || src;
      allTranslated[src] = tr;
      saveLocal(src, lang, tr);
    });
  }

  // Apply translations
  unknownEntries.forEach(({ node, source }, i) => {
    const m = source.match(/^(\s*)([\s\S]*?)(\s*)$/);
    const lead = m ? m[1] : '';
    const tail = m ? m[3] : '';
    const tr = allTranslated[unknown[i]] || unknown[i];
    const next = lead + tr + tail;
    if (node.nodeValue !== next) node.nodeValue = next;
  });
}

// Same as collectTextNodes but without the translatable filter — used on restore
function collectTextNodesAll(root) {
  const out = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      if (isInsideSkip(node)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  while ((n = walker.nextNode())) out.push(n);
  return out;
}

const AutoTranslator = () => {
  const { language } = useLanguage();
  const observerRef = useRef(null);
  const langRef = useRef(language);

  useEffect(() => {
    langRef.current = language;
    // Translate whole body on language change
    if (typeof document !== 'undefined') {
      translateRoot(document.body, language);
    }
  }, [language]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!('MutationObserver' in window)) return;

    const obs = new MutationObserver((mutations) => {
      if (langRef.current === 'en') return;
      // Debounce - many mutations arrive together during route change
      if (pendingTimer) clearTimeout(pendingTimer);
      pendingTimer = setTimeout(() => {
        // For added nodes, translate them
        const roots = new Set();
        mutations.forEach((m) => {
          m.addedNodes.forEach((n) => {
            if (n.nodeType === 1) roots.add(n);
            else if (n.nodeType === 3 && n.parentNode) roots.add(n.parentNode);
          });
        });
        roots.forEach((r) => translateRoot(r, langRef.current));
      }, 200);
    });
    obs.observe(document.body, { subtree: true, childList: true, characterData: false });
    observerRef.current = obs;
    return () => obs.disconnect();
  }, []);

  return null;
};

export default AutoTranslator;
